import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import PackageJson from "../../../package.json";
import { CardGames } from "../../components/atoms/GameCard";
import { DefaultLayout } from "../../layouts/DefaultLayout";
import { gameService } from "../../services/game.service";
import { gameStore } from "../../gStore/game.store";
import {
  CircleXIcon,
  CloseIcon,
  ControllerIcon,
  DislikeIcon,
  LikeIcon,
  NumberedListIcon,
  SearchIcon,
} from "../../components/atoms/Icons";
import { Col, Row, Input } from "antd";
import { filterList } from "../../constant";
import { Game } from "../../interfaces/game.interface";
import { CarouselGame } from "../../components/atoms/Carousel";
import { GCCarouselGame } from "../../components/atoms/GameCenterCarousel";
import { userStore } from "../../gStore/user.store";
import { GAMES_ENABLED } from "../../utilities/url";
import { randomGame } from "../../utilities/games";
import { FeedbackModal } from "../../components/atoms/FeedbackModal";
import { FilterButton } from "../../components/atoms/FilterButton";

type ISortableList = "released_at" | "trending";
const sortableList: ISortableList[] = ["released_at", "trending"];

export const Games = () => {
  const [showFeedback, setShowFeedback] = useState(false);
  const [selectedGame, setSelectedGame] = useState<Game>();
  const [loading, setLoading] = useState<boolean>(true);
  const [searchGame, setsearchGame] = useState<string>("");
  const [sort, setSort] = useState<ISortableList>("released_at");
  const [leaderboardSupportOnly, setLeaderboardSupportOnly] =
    useState<boolean>(false);
  const [filterSelect, setfilterSelect] = useState<string>("all");
  const [filterLiked, setFilterLiked] = useState<boolean | null>(null); // null means no filter, true means liked only, false means disliked only
  const [filterPlayed, setFilterPlayed] = useState<boolean | null>(null); // null means no filter, true means played only, false means unplayed only

  const onCloseModal = () => setShowFeedback(false);

  useEffect(() => {
    setLoading(true);
    gameService
      .getGames(userStore.data.user.id)
      .then((_games) => {
        gameStore.setGames({ payload: { games: _games } });
      })
      .catch((errMessage) => {
        console.log(errMessage);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    var queryParams = new URLSearchParams(window.location.search);
    if (sortableList.indexOf(queryParams.get("sort") as ISortableList) >= 0) {
      setSort(queryParams.get("sort") as ISortableList);
    }
  }, []);

  const clickFilter = useCallback(
    (e: string) => {
      if (e === filterSelect) {
        setfilterSelect("");
      } else {
        setfilterSelect(e);
      }
    },
    [filterSelect]
  );

  const filteredGames = useMemo(() => {
    // Start with all games sorted by selected sort
    let games: Game[] = [...gameStore.data.games].sort((a, b) =>
      a[sort] < b[sort] ? 1 : -1
    );

    // Apply search filter
    if (searchGame) {
      games = games.filter((game) =>
        game.id
          .toLocaleLowerCase()
          .startsWith(searchGame.toLocaleLowerCase())
      );
    }

    // Apply category filter
    if (filterSelect.toLocaleLowerCase() !== "all") {
      const filter = filterSelect.toLocaleLowerCase();
      games = games.filter((game) =>
        game.category.toLocaleLowerCase().includes(filter)
      );
    }

    // Apply leaderboard support filter
    if (leaderboardSupportOnly) {
      games = games.filter((game) => game.leaderboard_support);
    }

    // Apply liked/disliked filter
    if (filterLiked !== null) {
      games = games.filter((game) => game.is_liked === filterLiked);
    }

    // Apply played/unplayed filter
    if (filterPlayed !== null) {
      games = games.filter((game) => game.played === filterPlayed);
    }

    return games;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filterSelect,
    leaderboardSupportOnly,
    searchGame,
    sort,
    filterLiked,
    filterPlayed,

    // Linter doesn't like the gameStore dependency, but it is necessary for games to load properly
    gameStore.data.games,
  ]);

  return (
    <DefaultLayout title="List">
      {loading ? (
        <div className="w-full p-10 flex justify-center text-white">
          Loading...
        </div>
      ) : (
        <>
          <FeedbackModal
            game={selectedGame}
            showFeedback={showFeedback}
            onCloseModal={onCloseModal}
            setShowFeedback={setShowFeedback}
          />
          <section className="py-0">
            {GAMES_ENABLED ? (
              <CarouselGame games={gameStore.data.games} />
            ) : (
              <GCCarouselGame games={gameStore.data.games} />
            )}
          </section>
          <section className="py-8">
            <div className="container h-full">
              <Row gutter={[16, 24]} className="justify-start w-full">
                <Col span={18} className="w-full">
                  <div className="flex items-center gap-2 mb-6">
                    <h5 className="font-bold text-[20px] text-white">
                      {searchGame === ""
                        ? filterSelect.toLocaleLowerCase() === "all"
                          ? "All Games"
                          : `All ${filterSelect} games`
                        : "Searched Games"}
                    </h5>
                    <span className="font-medium text-[12px] px-2 py-[2px] rounded-2xl bg-[#2D333A]">
                      {filteredGames.length > 0 ? filteredGames.length : "0"}
                    </span>
                  </div>
                  <div
                    className={
                      "w-full h-[100%] overflow-x-hidden overflow-auto scrolStyle " +
                      (filteredGames.length > 0
                        ? ""
                        : "bg-[#282A2D] rounded-lg flex items-center justify-center")
                    }
                  >
                    {filteredGames.length > 0 ? (
                      <Row gutter={[16, 24]} className="pr-2">
                        {filteredGames.map((game) => {
                          return (
                            <Col key={game.id} span={24} md={12} lg={8}>
                              <CardGames
                                game={game}
                                setSelectedGame={setSelectedGame}
                                setShowFeedback={setShowFeedback}
                              />
                            </Col>
                          );
                        })}
                      </Row>
                    ) : (
                      <Fragment>
                        <div className="text-center">
                          <img
                            src={PackageJson.homepage + "/icons/Icon (2).svg"}
                            className="mx-auto mb-3"
                            alt=""
                          />
                          <h4 className="font-bold text-[20px] text-white mb-2">
                            No Such Game Found!
                          </h4>
                          <p className="text-[16px] text-white mb-6">
                            Try Searching another game.
                          </p>

                          <div
                            onClick={() => {
                              setsearchGame("");
                              setfilterSelect("all");
                            }}
                            className="inline-block font-semibold text-[12px] text-[#1A1C1E] bg-[#8DCA83] rounded-[5px] px-[24px] py-[10px] cursor-pointer"
                          >
                            Go Back
                          </div>
                        </div>
                      </Fragment>
                    )}
                  </div>
                </Col>
                <Col span={6}>
                  {GAMES_ENABLED ? (
                    <div className="mb-6 random-button">
                      <span
                        className="cursor-pointer font-bold text-[14px] bg-green-300 rounded-md px-6 py-2 w-fit text-center"
                        onClick={() => randomGame(gameStore.data.games)}
                      >
                        Play Random Game
                      </span>
                    </div>
                  ) : null}

                  <div className="mb-6">
                    <h5 className="font-bold text-[20px] text-white mb-6">
                      Filters
                    </h5>

                    <div className="flex items-center gap-2"></div>
                    <Input
                      onChange={(e) => setsearchGame(e.target.value)}
                      value={searchGame}
                      className={"text-[16px] filedSearch"}
                      placeholder="Search"
                      prefix={<SearchIcon />}
                      suffix={
                        searchGame === "" ? (
                          ""
                        ) : (
                          <div
                            className="cursor-pointer"
                            onClick={() => setsearchGame("")}
                          >
                            <CloseIcon />
                          </div>
                        )
                      }
                    />
                  </div>

                  <div className="mb-4 w-full">
                    <FilterButton
                      label="Leaderboard Support"
                      Icon={NumberedListIcon}
                      onClick={() => setLeaderboardSupportOnly(!leaderboardSupportOnly)}
                      selected={leaderboardSupportOnly === true}
                    />
                  </div>

                  <div className="mb-4 w-full flex flex-col lg:flex-row gap-2">
                    <FilterButton
                      label="Liked"
                      Icon={LikeIcon}
                      onClick={() =>
                        setFilterLiked(filterLiked === true ? null : true)
                      }
                      selected={filterLiked === true}
                    />
                    <FilterButton
                      label="Disliked"
                      Icon={DislikeIcon}
                      onClick={() =>
                        setFilterLiked(filterLiked === false ? null : false)
                      }
                      selected={filterLiked === false}
                    />
                  </div>

                  <div className="mb-4 w-full flex flex-col lg:flex-row gap-2">
                    <FilterButton
                      label="Played"
                      Icon={ControllerIcon}
                      onClick={() =>
                        setFilterPlayed(filterPlayed === true ? null : true)
                      }
                      selected={filterPlayed === true}
                    />
                    <FilterButton
                      label="Unplayed"
                      Icon={CircleXIcon}
                      onClick={() =>
                        setFilterPlayed(filterPlayed === false ? null : false)
                      }
                      selected={filterPlayed === false}
                    />
                  </div>

                  <h5 className="font-bold text-[20px] text-white mb-6">
                    Select Category
                  </h5>

                  <div className="w-full rounded-[8px] overflow-hidden bg-[#232628]">
                    {filterList.map((obj) => {
                      return (
                        <div
                          key={obj.title}
                          onClick={() => clickFilter(obj.title)}
                          className={
                            "rounded-[8px] cursor-pointer flex items-center p-4 gap-3 hover:bg-[#ffffff14] text-white " +
                            (obj.title.toLocaleLowerCase() ===
                            filterSelect.toLocaleLowerCase()
                              ? "bg-[#ffffff14]"
                              : "")
                          }
                        >
                          {obj.icon}{" "}
                          {obj.title.charAt(0).toUpperCase() +
                            obj.title.slice(1)}
                        </div>
                      );
                    })}
                  </div>
                </Col>
              </Row>
            </div>
          </section>
        </>
      )}
    </DefaultLayout>
  );
};
